import { db } from "../firebaseConfig";
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  increment,
} from "firebase/firestore";

/**
 * Records a view for a post by writing posts/{postId}/seenBy/{viewerId}.
 * Also stores readable user info so Firestore shows human names instead of raw UIDs.
 * Increments the post's views exactly once per viewer.
 *
 * @param {string} postId
 * @param {string} viewerId
 * @returns {Promise<boolean>} true if a new view was recorded
 */
export async function recordPostView(postId, viewerId) {
  if (!postId || !viewerId) return false;

  const postRef = doc(db, "posts", postId);
  const seenRef = doc(db, "posts", postId, "seenBy", viewerId);

  // ---------------------------------------------
  // Fetch readable user info from users/{viewerId}
  // ---------------------------------------------
  let userInfo = {
    userId: viewerId,
    userName: "Unknown",
    rank: "",
    agency: "",
  };

  try {
    const userRef = doc(db, "users", viewerId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const p = userSnap.data();
      userInfo = {
        userId: viewerId,
        userName: p.firstInitialLastName || "Unknown",
        rank: p.rank || "",
        agency: p.agency || "",
      };
    }
  } catch (e) {
    console.warn("Could not load viewer profile:", e);
  }

  // --------------------------------------------------
  // Use a transaction to ensure views increment only once
  // --------------------------------------------------
  try {
    const result = await runTransaction(db, async (transaction) => {
      const seenSnap = await transaction.get(seenRef);

      if (seenSnap.exists()) {
        return false; // already counted
      }

      // Write readable info
      transaction.set(seenRef, {
        ...userInfo,
        viewedAt: serverTimestamp(),
      });

      // Increment post view count
      transaction.update(postRef, {
        views: increment(1),
      });

      return true;
    });

    return result;
  } catch (err) {
    console.error("Failed to record post view:", err);
    throw err;
  }
}
